import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EnvironmentService } from '../../../core/services/environment.service';
import { AuthService } from '../../../core/services/auth.service';

interface EpTokenResponse {
  token: string;
  expiresAt: string;
}

interface EpTokenRequest {
  issueTypeId: number;
  crsId?: string;
  customerId?: string;
  userId?: string;
}

/**
 * Acquires EFUS upload token via Employee Portal's token proxy endpoint (§0.3).
 * Uses the EP session's Keycloak Bearer token for authentication.
 *
 * POST {employeePortalApiUrl}/api/v1/fileupload/token
 * Auth: Bearer <Keycloak EP session token>
 */
@Injectable({ providedIn: 'root' })
export class EfusTokenService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvironmentService);
  private readonly authService = inject(AuthService);

  private cachedToken: string | null = null;
  private expiresAt: Date | null = null;

  getToken(): Observable<string> {
    if (this.cachedToken && this.expiresAt && new Date() < this.expiresAt) {
      return of(this.cachedToken);
    }
    return this.mint();
  }

  private mint(): Observable<string> {
    const epBaseUrl = this.env.employeePortalApiUrl;

    const body: EpTokenRequest = {
      issueTypeId: 4,
    };

    // Use EP's Keycloak session token — the EP backend resolves customerId/userId from it
    const epToken = this.authService.token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(epToken ? { Authorization: `Bearer ${epToken}` } : {}),
      'x-skip-auth': 'true', // Skip the app's own auth interceptor to avoid double-attaching
    });

    return this.http
      .post<EpTokenResponse>(`${epBaseUrl}/api/v1/fileupload/token`, body, { headers })
      .pipe(
        tap((response) => {
          this.cachedToken = response.token;
          this.expiresAt = new Date(new Date(response.expiresAt).getTime() - 60_000);
        }),
        map((response) => response.token),
      );
  }

  clear(): void {
    this.cachedToken = null;
    this.expiresAt = null;
  }
}
